import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import ViewToggle, { readViewPreference } from '../../../components/common/ViewToggle';
import CourseNameBadge from '../../../components/common/CourseNameBadge';
import EnrollmentAnalytics from '../../../components/analytics/EnrollmentAnalyticsV2';
import EnrollmentCollectionsAnalytics from '../../../components/analytics/EnrollmentCollectionsAnalytics';

const BUILTIN_CERT_KEYS = new Set([
  'studentName', 'studentEmail', 'courseName', 'courseCode', 'completionDate',
  'certificateNumber', 'grade', 'batchNumber', 'enrollmentNumber',
  'tenantName', 'tenantLogoUrl', 'issuerName', 'issuerTitle',
  'verifyUrl', 'qrDataUrl', 'certificateTitle', 'subtitle',
]);

// ─── Constants ────────────────────────────────────────────────────────────────

const PAYMENT_METHODS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'card',          label: 'Card' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'dd',            label: 'Demand Draft' },
  { value: 'online',        label: 'Online' },
  { value: 'other',         label: 'Other' },
];

const ENROLLMENT_STATUSES = [
  { value: 'active',        label: 'Active' },
  { value: 'started',       label: 'Started' },
  { value: 'ongoing',       label: 'Ongoing' },
  { value: 'completed',     label: 'Completed' },
  { value: 'discontinued',  label: 'Discontinued' },
  { value: 'cancelled',     label: 'Cancelled' },
];

const PAYMENT_STATUS_BS = {
  pending:   'warning',
  partial:   'info',
  completed: 'success',
  overdue:   'danger',
};

const ENROLLMENT_STATUS_BS = {
  active:        'primary',
  started:       'info',
  ongoing:       'info',
  completed:     'success',
  discontinued:  'warning',
  cancelled:     'danger',
};

const fmt = (n) => (n !== undefined && n !== null ? `₹${Number(n).toLocaleString('en-IN')}` : '₹0');
const paymentMethodLabel = (v) => PAYMENT_METHODS.find(m => m.value === v)?.label || v || '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN') : '—';

// ─── BSModal Helper ───────────────────────────────────────────────────────────

const buildCountOptions = (values = []) => {
  const counts = new Map();
  values
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .forEach((value) => counts.set(value, Number(counts.get(value) || 0) + 1));
  return Array.from(counts.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, label: `${value} (${count})` }));
};

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div
      className="modal fade show d-block"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── Payment History Dialog ───────────────────────────────────────────────────

const PaymentHistoryDialog = ({ show, enrollment, onClose, onViewReceipt, onAlert }) => {
  const [data, setData]     = useState(null);
  const [resending, setResending] = useState({});

  useEffect(() => {
    if (!show || !enrollment) return;
    api.get(`/training/enrollments/${enrollment._id}/payments`)
      .then(r => setData(r.data?.data || r.data))
      .catch(() => {});
    return () => setData(null);
  }, [show, enrollment]);

  const handleResend = async (paymentId) => {
    setResending(p => ({ ...p, [paymentId]: true }));
    try {
      const r = await api.post(`/training/enrollments/${enrollment._id}/payment/${paymentId}/resend-receipt`, {});
      setData(current => current ? {
        ...current,
        paymentHistory: (current.paymentHistory || []).map(item =>
          item.paymentId === paymentId || item._id === paymentId
            ? {
                ...item,
                receiptSent: true,
                receiptSentAt: new Date().toISOString(),
                receiptEmailStatus: 'sent',
                receiptEmailError: '',
              }
            : item
        ),
      } : current);
      onAlert?.(r.data?.message || 'Receipt emailed successfully.', 'success');
    } catch (err) {
      onAlert?.(err.response?.data?.message || 'Receipt email could not be sent.', 'danger');
    } finally {
      setResending(p => ({ ...p, [paymentId]: false }));
    }
  };

  // Download the receipt PDF with the JWT attached, then trigger a
  // browser save. We can't just point an <a href> at the API
  // endpoint because the browser would send the request without
  // our Authorization header and the server would 401.
  const handleDownloadPdf = async (paymentId, receiptNumber) => {
    try {
      const r = await api.get(
        `/training/enrollments/${enrollment._id}/receipt/${paymentId}/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Receipt-${receiptNumber || paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[receipt] PDF download failed:', err);
    }
  };

  const history = data?.paymentHistory || [];

  // Guard against null/undefined enrollment. The parent uses
  // `show={!!...}` but the prop can race, so we bail out early
  // rather than throwing when something downstream touches _id.
  if (!enrollment) return null;

  return (
    <BSModal show={show} onClose={onClose} title={`Payment History — ${enrollment.enrollmentNumber}`} size="modal-xl">
      {history.length === 0 ? (
        <div className="alert alert-info">No payments recorded yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Date & Time</th>
                <th>Transaction ID</th>
                <th>Receipt No.</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Instalment</th>
                <th>Status</th>
                <th>Receipt Email</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p, idx) => (
                <tr key={idx}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div>{fmtDate(p.paymentDate)}</div>
                    <small className="text-muted">
                      {p.paymentDate ? new Date(p.paymentDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </small>
                  </td>
                  <td><code className="small">{p.paymentId || '—'}</code></td>
                  <td><code className="small">{p.receiptNumber || '—'}</code></td>
                  <td><strong>{fmt(p.amount)}</strong></td>
                  <td>{paymentMethodLabel(p.method || p.paymentMethod)}</td>
                  <td>{p.installmentNumber ? `#${p.installmentNumber}` : 'Full'}</td>
                  <td>
                    <span className={`badge bg-${p.status === 'completed' ? 'success' : p.status === 'pending' ? 'warning text-dark' : p.status === 'failed' ? 'danger' : 'secondary'}`}>
                      {p.status || 'recorded'}
                    </span>
                  </td>
                  <td>
                    {p.receiptSent ? (
                      <span className="text-success small">
                        <i className="bi bi-check-circle me-1" />
                        {fmtDate(p.receiptSentAt)}
                      </span>
                    ) : p.receiptEmailStatus === 'failed' ? (
                      <span className="text-danger small" title={p.receiptEmailError || 'Email delivery failed'}>
                        <i className="bi bi-exclamation-circle me-1" />
                        Failed
                      </span>
                    ) : (
                      <span className="text-muted small">
                        <i className="bi bi-hourglass me-1" />
                        Not sent
                      </span>
                    )}
                  </td>
                  <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                    {p.paymentId && (
                      <>
                        <button
                          className="btn btn-outline-secondary btn-sm me-1"
                          title="View Receipt"
                          onClick={() => onViewReceipt && onViewReceipt(enrollment, p.paymentId)}
                        >
                          <i className="bi bi-receipt" />
                        </button>
                        <button
                          className="btn btn-outline-success btn-sm me-1"
                          title="Download PDF"
                          onClick={() => handleDownloadPdf(p.paymentId, p.receiptNumber)}
                        >
                          <i className="bi bi-file-earmark-pdf" />
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          title="Resend Receipt"
                          disabled={resending[p.paymentId]}
                          onClick={() => handleResend(p.paymentId)}
                        >
                          {resending[p.paymentId]
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-envelope" />}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BSModal>
  );
};

// ─── Instalment Schedule Dialog ───────────────────────────────────────────────

const InstalmentScheduleDialog = ({ show, enrollment, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!show || !enrollment) return;
    api.get(`/training/enrollments/${enrollment._id}/emi-schedule`)
      .then(r => setData(r.data?.data || r.data))
      .catch(() => {});
    return () => setData(null);
  }, [show, enrollment]);

  const schedule = data?.emiSchedule || enrollment?.fees?.emiSchedule || [];

  const statusColor = { paid: 'success', partial: 'info', overdue: 'danger', upcoming: 'secondary', pending: 'warning' };

  // Guard against null enrollment.
  if (!enrollment) return null;

  return (
    <BSModal show={show} onClose={onClose} title={`Instalment Schedule — ${enrollment.enrollmentNumber}`} size="modal-lg">
      {schedule.length === 0 ? (
        <div className="alert alert-info">No instalment schedule available.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Paid Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((inst, idx) => (
                <tr key={idx}>
                  <td>{inst.installmentNumber}</td>
                  <td>{fmtDate(inst.dueDate)}</td>
                  <td>{fmt(inst.amount)}</td>
                  <td>{fmt(inst.paidAmount)}</td>
                  <td>{fmt((inst.amount || 0) - (inst.paidAmount || 0))}</td>
                  <td>{inst.paidDate ? fmtDate(inst.paidDate) : '—'}</td>
                  <td>
                    <span className={`badge bg-${statusColor[inst.status] || 'secondary'}`}>
                      {inst.status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BSModal>
  );
};

// ─── Receipt Dialog ───────────────────────────────────────────────────────────

// ─── Convert number to words (Indian numbering) ─────────────────────────────
const numToWordsINR = (num) => {
  if (num == null || isNaN(num)) return '';
  const n = Math.floor(Number(num));
  if (n === 0) return 'Zero Rupees Only';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x) => x < 20 ? ones[x] : tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '');
  const three = (x) => x >= 100 ? ones[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + two(x % 100) : '') : two(x);
  let words = '';
  const crore = Math.floor(n / 10000000);
  const lakh  = Math.floor((n % 10000000) / 100000);
  const thou  = Math.floor((n % 100000) / 1000);
  const rest  = n % 1000;
  if (crore) words += three(crore) + ' Crore ';
  if (lakh)  words += three(lakh) + ' Lakh ';
  if (thou)  words += three(thou) + ' Thousand ';
  if (rest)  words += three(rest);
  return words.trim() + ' Rupees Only';
};

const ReceiptDialog = ({ show, enrollment, paymentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [receiptHtml, setReceiptHtml] = useState('');

  // We render the SAME branded HTML that the backend uses for the
  // PDF and the email body. This keeps the on-screen preview,
  // downloaded PDF and emailed receipt visually identical.
  //
  // We fetch the HTML via axios (which attaches the JWT) and then
  // render it inside an iframe via `srcdoc` — this avoids the
  // cross-origin issue we'd hit if we tried to point the iframe
  // directly at the API endpoint (browsers don't attach the
  // Authorization header to sub-resource requests like iframes).
  const apiBase = api.defaults?.baseURL || '';
  const pdfUrl  = enrollment
    ? `${apiBase}/training/enrollments/${enrollment._id}/receipt/${paymentId}/pdf`
    : '';

  useEffect(() => {
    if (!show || !enrollment || !paymentId) return;
    setLoading(true);
    setError(null);
    setReceiptHtml('');
    api.get(`/training/enrollments/${enrollment._id}/receipt/${paymentId}/html`, {
      responseType: 'text',
      transformResponse: [(d) => d],
    })
      .then(r => {
        // The backend builds <img src="…/uploads/company/…"> URLs.
        // When we render the HTML inside an iframe via srcdoc the
        // browser can't attach our JWT, so the upload middleware
        // would 401 the image. Append the token as ?t= (the
        // middleware already supports this for the same reason).
        const token = localStorage.getItem('token');
        let html = r.data;
        if (token) {
          html = html.replace(/(<img\s+[^>]*src="[^"]*\/uploads\/[^"]*)"/g,
            (_, p1) => `${p1}?t=${token}"`);
        }
        setReceiptHtml(html);
      })
      .catch(err => setError(err.response?.data?.message || err.message || 'Failed to load receipt.'))
      .finally(() => setLoading(false));
  }, [show, enrollment, paymentId]);

  // Guard against null/undefined enrollment — bail out cleanly when
  // the dialog is mounted without a valid enrollment record (the
  // parent uses `show={!!dlgReceipt.enr}` but the prop can race).
  // NOTE: this MUST come after all hooks so React's Rules of Hooks
  // are satisfied (hooks must be called in the same order every render).
  if (!enrollment) return null;

  // Download the PDF with the JWT attached so the server lets us in.
  // (Browsers don't send the Authorization header on direct
  // navigation, hence the blob dance.)
  const handleDownloadPdf = async () => {
    try {
      const r = await api.get(
        `/training/enrollments/${enrollment._id}/receipt/${paymentId}/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `Receipt-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[receipt] PDF download failed:', err);
    }
  };

  const openPrintWindow = () => {
    if (!receiptHtml) return;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    w.document.write(receiptHtml);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 500);
  };

  return (
    <BSModal show={show} onClose={onClose} title="Payment Receipt" size="modal-lg"
      footer={
        <>
          <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Close</button>
          <button className="btn btn-outline-primary btn-sm" onClick={handleDownloadPdf}>
            <i className="bi bi-file-earmark-pdf me-1" />Download PDF
          </button>
          <button className="btn btn-primary btn-sm" onClick={openPrintWindow} disabled={!receiptHtml}>
            <i className="bi bi-printer me-1" />Print Receipt
          </button>
        </>
      }
    >
      <div style={{
        position: 'relative',
        width: '100%',
        height: '70vh',
        minHeight: 480,
        background: '#f3f4f6',
        borderRadius: 6,
        overflow: 'hidden',
      }}>
        {loading && (
          <div className="text-center py-5" style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
            <div className="spinner-border text-primary" />
            <div className="text-muted small mt-2">Generating branded receipt…</div>
          </div>
        )}
        {error && (
          <div className="alert alert-danger m-3" style={{ position: 'absolute', inset: 0, zIndex: 3 }}>
            <strong>Couldn't load receipt.</strong> {error}
          </div>
        )}
        {receiptHtml && (
          <iframe
            title="Payment Receipt"
            srcDoc={receiptHtml}
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              background: '#fff',
              display: loading ? 'none' : 'block',
            }}
          />
        )}
      </div>
    </BSModal>
  );
};

// ─── Add Payment Dialog ───────────────────────────────────────────────────────

const AddPaymentDialog = ({ show, enrollment, onClose, onSuccess, onAlert }) => {
  const [form, setForm] = useState({ amount: '', paymentMethod: 'cash', paymentDate: '', referenceNumber: '', instalmentNumber: '', remarks: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) setForm({
      amount: '',
      paymentMethod: 'cash',
      paymentDate: new Date().toISOString().slice(0, 10),
      referenceNumber: '',
      instalmentNumber: '',
      remarks: '',
    });
  }, [show]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.amount || isNaN(Number(form.amount))) { onAlert('Enter a valid amount.', 'danger'); return; }
    if (!form.paymentDate) { onAlert('Please select the transaction date.', 'danger'); return; }
    setSaving(true);
    try {
      // Backend field names: method / transactionReference / installmentNumber
      const r = await api.post(`/training/enrollments/${enrollment._id}/payment`, {
        amount:                Number(form.amount),
        method:                form.paymentMethod,
        paymentDate:           form.paymentDate,
        transactionReference:  form.referenceNumber || undefined,
        installmentNumber:     form.instalmentNumber ? Number(form.instalmentNumber) : undefined,
        remarks:               form.remarks || undefined,
      });
      if (r.data?.success !== false) {
        const payload = r.data?.data || r.data;
        onSuccess(payload?.enrollment || payload, payload?.receipt);
        onClose();
      }
      else onAlert(r.data?.message || 'Failed to record payment.', 'danger');
    } catch (e) { onAlert(e.response?.data?.message || 'Failed to record payment.', 'danger'); }
    setSaving(false);
  };

  const balance = enrollment?.fees?.balance ?? enrollment?.fees?.balanceAmount;

  return (
    <BSModal show={show} onClose={onClose} title={`Add Payment — ${enrollment?.enrollmentNumber}`} size="modal-md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-success" onClick={handleSubmit} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Record Payment'}
          </button>
        </>
      }
    >
      {balance !== undefined && (
        <div className="alert alert-info py-2 small mb-3">
          Balance due: <strong>{fmt(balance)}</strong>
        </div>
      )}
      <div className="row g-3">
        <div className="col-6">
          <label className="form-label">Amount (₹) <span className="text-danger">*</span></label>
          <input className="form-control" type="number" min="1" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0" />
        </div>
        <div className="col-6">
          <label className="form-label">Payment Method</label>
          <select className="form-select" value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
            {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="col-6">
          <label className="form-label">Transaction Date <span className="text-danger">*</span></label>
          <input className="form-control" type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
        </div>
        <div className="col-6">
          <label className="form-label">Reference No.</label>
          <input className="form-control" value={form.referenceNumber} onChange={e => set('referenceNumber', e.target.value)} placeholder="Transaction ID / Cheque No." />
        </div>
        <div className="col-6">
          <label className="form-label">Instalment #</label>
          <input className="form-control" type="number" min="1" value={form.instalmentNumber} onChange={e => set('instalmentNumber', e.target.value)} placeholder="(optional)" />
        </div>
        <div className="col-12">
          <label className="form-label">Remarks</label>
          <textarea className="form-control" rows={2} value={form.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Optional notes" />
        </div>
      </div>
    </BSModal>
  );
};

// ─── Cancel Dialog ────────────────────────────────────────────────────────────

const CancelDialog = ({ show, enrollment, onClose, onSuccess, onAlert }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (show) setReason(''); }, [show]);

  const handleCancel = async () => {
    if (!reason.trim()) { onAlert('Please provide a reason for cancellation.', 'danger'); return; }
    setSaving(true);
    try {
      const r = await api.put(`/training/enrollments/${enrollment._id}/cancel`, { cancellationReason: reason });
      if (r.data?.success !== false) { onSuccess(r.data?.data || r.data); onClose(); }
      else onAlert(r.data?.message || 'Failed to cancel.', 'danger');
    } catch (e) { onAlert(e.response?.data?.message || 'Failed to cancel.', 'danger'); }
    setSaving(false);
  };

  return (
    <BSModal show={show} onClose={onClose} title={`Cancel Enrollment — ${enrollment?.enrollmentNumber}`} size="modal-md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Keep Enrollment</button>
          <button className="btn btn-danger" onClick={handleCancel} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Cancelling…</> : 'Confirm Cancel'}
          </button>
        </>
      }
    >
      <div className="alert alert-warning small">
        <i className="bi bi-exclamation-triangle me-1" />
        Cancelling an enrollment is irreversible. Any paid fees will need manual processing for refund.
      </div>
      <label className="form-label">Reason for Cancellation <span className="text-danger">*</span></label>
      <textarea
        className="form-control"
        rows={3}
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Describe the reason for cancellation…"
      />
    </BSModal>
  );
};

// ─── Status Change Dialog ─────────────────────────────────────────────────────

const StatusChangeDialog = ({ show, enrollment, onClose, onSuccess, onAlert, canAdmin }) => {
  const [status, setStatus]   = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (show && enrollment) { setStatus(enrollment.status || 'active'); setRemarks(''); }
  }, [show, enrollment]);

  const handleSave = async () => {
    if (status === 'discontinued' && !remarks.trim()) { onAlert('Please provide remarks for discontinuation.', 'danger'); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/training/enrollments/${enrollment._id}/status`, { status, remarks: remarks || undefined });
      if (r.data?.success !== false) { onSuccess(r.data?.data || r.data); onClose(); }
      else onAlert(r.data?.message || 'Failed to update status.', 'danger');
    } catch (e) { onAlert(e.response?.data?.message || 'Failed to update status.', 'danger'); }
    setSaving(false);
  };

  return (
    <BSModal show={show} onClose={onClose} title="Change Enrollment Status" size="modal-sm"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Update Status'}
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label">New Status</label>
        <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
          {ENROLLMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div>
        <label className="form-label">Remarks {status === 'discontinued' && <span className="text-danger">*</span>}</label>
        <textarea className="form-control" rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Optional" />
      </div>
    </BSModal>
  );
};

// ─── Fee Info Dialog ──────────────────────────────────────────────────────────

const FeeInfoDialog = ({ show, enrollment, onClose }) => (
  <BSModal show={show} onClose={onClose} title="Fee Details" size="modal-sm">
    {enrollment?.fees && (
      <table className="table table-sm table-borderless">
        <tbody>
          <tr><td className="text-muted">Original Fees</td><td className="text-end fw-bold">{fmt(enrollment.fees.originalFees)}</td></tr>
          <tr><td className="text-muted">Discount</td><td className="text-end text-danger">–{fmt(enrollment.fees.discountAmount)}</td></tr>
          <tr><td className="text-muted fw-bold">Total Fees</td><td className="text-end fw-bold">{fmt(enrollment.fees.total ?? enrollment.fees.totalFees)}</td></tr>
          <tr><td className="text-muted">Paid</td><td className="text-end text-success">{fmt(enrollment.fees.paid ?? enrollment.fees.paidAmount)}</td></tr>
          <tr><td className="text-muted">Balance</td><td className="text-end text-danger">{fmt(enrollment.fees.balance ?? enrollment.fees.balanceAmount)}</td></tr>
          <tr>
            <td className="text-muted">Status</td>
            <td className="text-end">
              <span className={`badge bg-${PAYMENT_STATUS_BS[enrollment.paymentStatus] || 'secondary'}`}>
                {enrollment.paymentStatus}
              </span>
            </td>
          </tr>
          {enrollment.fees.paymentMode === 'emi' && (
            <tr><td className="text-muted">EMI</td><td className="text-end">{enrollment.fees.emiMonths} months</td></tr>
          )}
        </tbody>
      </table>
    )}
  </BSModal>
);

// ─── Edit Enrollment Dialog ───────────────────────────────────────────────────

const MembershipSubscriptionDialog = ({ show, enrollment, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !enrollment) return;
    setLoading(true);
    setSummary(null);
    setError('');
    api.get('/training/memberships/subscription-summary', {
      params: {
        subscriptionId: enrollment.sourceMeta?.membershipSubscriptionId || '',
        orderId: enrollment.sourceMeta?.membershipOrderId || '',
        planId: enrollment.sourceMeta?.membershipPlanId || '',
      },
    })
      .then((r) => setSummary(r.data?.data || null))
      .catch((e) => setError(e.response?.data?.message || 'Could not load membership details.'))
      .finally(() => setLoading(false));
  }, [show, enrollment]);

  return (
    <BSModal show={show} onClose={onClose} title="Membership Subscription Details" size="modal-lg">
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : summary ? (
        <div className="d-flex flex-column gap-3">
          <div className="border text-white" style={{ borderRadius: 20, padding: 20, background: 'linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)', borderColor: 'rgba(255,255,255,0.12)' }}>
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <div className="small" style={{ color: 'rgba(255,255,255,0.75)' }}>Membership</div>
                <div className="fw-semibold" style={{ fontSize: 24, lineHeight: 1.2 }}>{summary.plan?.name || enrollment.sourceMeta?.membershipPlanName || '-'}</div>
                <div className="small mt-2">{summary.plan?.code || enrollment.sourceMeta?.membershipPlanCode || '-'}</div>
              </div>
              <div className="text-end">
                <div className="small" style={{ color: 'rgba(255,255,255,0.75)' }}>Status</div>
                <div className="fw-semibold text-capitalize">{summary.subscription?.status || '-'}</div>
                <div className="small mt-2">Paid {fmt(summary.order?.amount || summary.plan?.salePrice || summary.plan?.price || 0)}</div>
              </div>
            </div>
          </div>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <div className="border h-100" style={{ borderRadius: 18, padding: 18, background: '#f8fbff', borderColor: '#dbeafe' }}>
                <div className="fw-semibold mb-3">Subscription Window</div>
                <div className="row g-3">
                  <div className="col-6"><div className="small text-muted">Start Date</div><div className="fw-semibold">{fmtDate(summary.subscription?.startsAt)}</div></div>
                  <div className="col-6"><div className="small text-muted">Expiry Date</div><div className="fw-semibold">{fmtDate(summary.subscription?.expiresAt)}</div></div>
                  <div className="col-6"><div className="small text-muted">Plan Price</div><div className="fw-semibold">{fmt(summary.plan?.salePrice || summary.plan?.price || 0)}</div></div>
                  <div className="col-6"><div className="small text-muted">Coupon</div><div className="fw-semibold">{summary.order?.couponCode || '-'}</div></div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="border h-100" style={{ borderRadius: 18, padding: 18, background: '#fafafc', borderColor: '#e2e8f0' }}>
                <div className="fw-semibold mb-3">Payment Summary</div>
                <div className="row g-3">
                  <div className="col-6"><div className="small text-muted">Base Amount</div><div className="fw-semibold">{fmt(summary.order?.baseAmount || 0)}</div></div>
                  <div className="col-6"><div className="small text-muted">Discount</div><div className="fw-semibold text-success">{fmt(summary.order?.discountAmount || 0)}</div></div>
                  <div className="col-6"><div className="small text-muted">Provider</div><div className="fw-semibold text-capitalize">{summary.order?.provider || '-'}</div></div>
                  <div className="col-6"><div className="small text-muted">Paid Date</div><div className="fw-semibold">{fmtDate(summary.order?.paidAt)}</div></div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="border" style={{ borderRadius: 18, padding: 18, background: '#fff', borderColor: '#e2e8f0' }}>
                <div className="fw-semibold mb-3">Transaction Details</div>
                <div className="row g-3">
                  <div className="col-md-4"><div className="small text-muted">Payment Status</div><div className="fw-semibold text-capitalize">{String(summary.order?.providerStatus || summary.order?.status || '-').replaceAll('_', ' ')}</div></div>
                  <div className="col-md-4"><div className="small text-muted">Order Reference</div><div className="fw-semibold text-break">{summary.order?.providerOrderId || '-'}</div></div>
                  <div className="col-md-4"><div className="small text-muted">Payment Reference</div><div className="fw-semibold text-break">{summary.order?.providerPaymentId || '-'}</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted">No subscription details found.</div>
      )}
    </BSModal>
  );
};

const EditEnrollmentDialog = ({ show, enrollment, courses, batches, onClose, onSuccess, onAlert, canAdmin }) => {
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [filteredBatches, setFilteredBatches] = useState([]);

  useEffect(() => {
    if (!show || !enrollment) return;
    setForm({
      courseId:        enrollment.course?._id || '',
      batchId:         enrollment.batch?._id  || '',
      status:          enrollment.status      || 'active',
      paymentMode:     enrollment.paymentType === 'emi' ? 'emi' : (enrollment.fees?.paymentMode || 'full'),
      paymentMethod:   enrollment.paymentMethod || 'cash',
      totalFees:       enrollment.fees?.total ?? enrollment.fees?.totalFees ?? '',
      discountAmount:  enrollment.fees?.discount ?? enrollment.fees?.discountAmount ?? '',
      emiMonths:       enrollment.emiDetails?.numberOfInstallments ?? enrollment.fees?.emiMonths ?? '',
      notes:           enrollment.notes               || '',
    });
  }, [show, enrollment]);

  useEffect(() => {
    if (form.courseId) setFilteredBatches(batches.filter(b => b.courseId === form.courseId || b.course?._id === form.courseId));
  }, [form.courseId, batches]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Backend PUT accepts: course, batch, paymentType, paymentMethod,
      // emiDetails, fees:{discount}, status. Total fees cannot be changed
      // after creation — only discount can (see backend validation).
      const payload = {
        course:      form.courseId || undefined,
        batch:       form.batchId  || undefined,
        status:      form.status,
        paymentType: form.paymentMode === 'emi' ? 'emi' : 'one_time',
        fees: { discount: Number(form.discountAmount) || 0 },
        notes:       form.notes || undefined,
      };
      if (form.paymentMethod) payload.paymentMethod = form.paymentMethod;
      if (form.paymentMode === 'emi' && form.emiMonths) {
        payload.emiDetails = { numberOfInstallments: Number(form.emiMonths) };
      }
      const r = await api.put(`/training/enrollments/${enrollment._id}`, payload);
      if (r.data?.success !== false) { onSuccess(r.data?.data || r.data); onClose(); }
      else onAlert(r.data?.message || 'Failed to update.', 'danger');
    } catch (e) { onAlert(e.response?.data?.message || 'Failed to update.', 'danger'); }
    setSaving(false);
  };

  return (
    <BSModal show={show} onClose={onClose} title={`Edit Enrollment — ${enrollment?.enrollmentNumber}`} size="modal-lg"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : 'Save Changes'}
          </button>
        </>
      }
    >
      <div className="row g-3">
        {/* Student info (read-only) */}
        <div className="col-12">
          <div className="alert alert-light py-2 small mb-0">
            <strong>Student:</strong> {enrollment?.student?.firstName} {enrollment?.student?.lastName}
            {enrollment?.student?.email && <span className="ms-2 text-muted">{enrollment.student.email}</span>}
          </div>
        </div>
        {/* Course */}
        <div className="col-6">
          <label className="form-label">Course</label>
          <select className="form-select" value={form.courseId || ''} onChange={e => { set('courseId', e.target.value); set('batchId', ''); }}>
            <option value="">Select Course</option>
            {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name} {c.courseCode ? `(${c.courseCode})` : ''}</option>)}
          </select>
        </div>
        {/* Batch */}
        <div className="col-6">
          <label className="form-label">Batch</label>
          <select className="form-select" value={form.batchId || ''} onChange={e => set('batchId', e.target.value)}>
            <option value="">Select Batch</option>
            {filteredBatches.map(b => <option key={b._id} value={b._id}>{b.batchNumber}</option>)}
          </select>
        </div>
        {/* Status */}
        <div className="col-6">
          <label className="form-label">Status</label>
          <select className="form-select" value={form.status || ''} onChange={e => set('status', e.target.value)}>
            {ENROLLMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {/* Payment Mode */}
        <div className="col-6">
          <label className="form-label">Payment Mode</label>
          <select className="form-select" value={form.paymentMode || 'full'} onChange={e => set('paymentMode', e.target.value)}>
            <option value="full">Full Payment</option>
            <option value="partial">Partial</option>
            <option value="emi">EMI</option>
          </select>
        </div>
        {/* Total Fees */}
        <div className="col-6">
          <label className="form-label">Total Fees (₹)</label>
          <input className="form-control" type="number" value={form.totalFees || ''} onChange={e => set('totalFees', e.target.value)} />
        </div>
        {/* Discount */}
        <div className="col-6">
          <label className="form-label">Discount (₹)</label>
          <input className="form-control" type="number" value={form.discountAmount || ''} onChange={e => set('discountAmount', e.target.value)} />
        </div>
        {/* EMI months */}
        {form.paymentMode === 'emi' && (
          <div className="col-6">
            <label className="form-label">EMI Months</label>
            <input className="form-control" type="number" min="1" value={form.emiMonths || ''} onChange={e => set('emiMonths', e.target.value)} />
          </div>
        )}
        {/* Notes */}
        <div className="col-12">
          <label className="form-label">Notes</label>
          <textarea className="form-control" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
    </BSModal>
  );
};

// ─── New Enrollment Form ──────────────────────────────────────────────────────

const blankEnrForm = {
  isNewStudent:      false,
  studentId:         '',
  firstName:         '',
  lastName:          '',
  email:             '',
  phone:             '',
  courseId:          '',
  batchId:           '',
  paymentMode:       'full',
  paymentMethod:     'cash',
  totalFees:         '',
  discountAmount:    '',
  emiMonths:         '',
  joiningDate:       new Date().toISOString().split('T')[0],
  notes:             '',
};

// ─── Main Component ───────────────────────────────────────────────────────────

const blankIssueField = { label: '', key: '', type: 'text', maxStars: 5 };

const buildInitialCertificateForm = (enrollment) => ({
  templateId: '',
  studentName: `${enrollment?.student?.firstName || ''} ${enrollment?.student?.lastName || ''}`.trim(),
  studentEmail: enrollment?.student?.email || '',
  courseName: enrollment?.course?.courseName || enrollment?.course?.name || '',
  batchNumber: enrollment?.batch?.batchNumber || '',
  completionDate: (() => {
    const raw = enrollment?.completionDate || enrollment?.batch?.endDate || new Date();
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return new Date().toISOString().split('T')[0];
    return parsed.toISOString().split('T')[0];
  })(),
  grade: '',
  issuerName: '',
  issuerTitle: '',
  certificateTitle: '',
  subtitle: '',
});

const IssueCertificateDialog = ({ show, enrollment, templates, onClose, onSuccess, onAlert }) => {
  const [form, setForm] = useState(() => buildInitialCertificateForm(enrollment));
  const [busy, setBusy] = useState(false);
  const [extraIssueFields, setExtraIssueFields] = useState([]);
  const [hiddenTemplateFields, setHiddenTemplateFields] = useState([]);
  const [newField, setNewField] = useState(blankIssueField);

  useEffect(() => {
    if (!show || !enrollment) return;
    setForm(buildInitialCertificateForm(enrollment));
    setExtraIssueFields([]);
    setHiddenTemplateFields([]);
    setNewField(blankIssueField);
    setBusy(false);
  }, [show, enrollment]);

  if (!enrollment) return null;

  const selectedTemplate =
    templates.find(t => t._id === form.templateId)
    || templates.find(t => t.isDefault)
    || templates[0]
    || null;

  const customTemplateFields = (() => {
    const raw = selectedTemplate?.fields || [];
    const seen = new Set();
    return raw.filter((field) => {
      if (!field || field.isStatic || !field.key || field.type === 'mask') return false;
      if (BUILTIN_CERT_KEYS.has(field.key)) return false;
      if (seen.has(field.key)) return false;
      seen.add(field.key);
      return true;
    });
  })();

  const visibleCustomFields = (() => {
    const baseFields = customTemplateFields.filter(f => !hiddenTemplateFields.includes(f.key));
    const merged = [...baseFields];
    extraIssueFields.forEach((field) => {
      if (!merged.some(x => x.key === field.key)) merged.push(field);
    });
    return merged;
  })();

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const removeIssueField = (field) => {
    if (!field?.key) return;
    if (extraIssueFields.some(f => f.key === field.key)) {
      setExtraIssueFields(list => list.filter(f => f.key !== field.key));
    } else {
      setHiddenTemplateFields(list => list.includes(field.key) ? list : [...list, field.key]);
    }
    setField(field.key, '');
  };

  const addIssueField = () => {
    const label = (newField.label || '').trim();
    const key = (newField.key || '').trim().replace(/[^a-zA-Z0-9_]/g, '_');
    if (!label) return onAlert('Field label is required', 'danger');
    if (!key) return onAlert('Field key is required', 'danger');
    if (BUILTIN_CERT_KEYS.has(key) || visibleCustomFields.some(f => f.key === key)) {
      return onAlert('Field key already exists', 'danger');
    }
    setExtraIssueFields(list => [...list, {
      key,
      label,
      type: newField.type || 'text',
      maxStars: Number(newField.maxStars || 5),
    }]);
    setNewField(blankIssueField);
  };

  const previewIssuedCertificate = async (recordId, certificateNumber) => {
    const res = await api.get(`/training/certificates/${recordId}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${certificateNumber || 'certificate'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const extras = {
        studentName: form.studentName || '',
        studentEmail: form.studentEmail || '',
        courseName: form.courseName || '',
        batchNumber: form.batchNumber || '',
        completionDate: form.completionDate || '',
        grade: form.grade || '',
        certificateTitle: form.certificateTitle || '',
        subtitle: form.subtitle || '',
        issuerName: form.issuerName || '',
        issuerTitle: form.issuerTitle || '',
      };
      visibleCustomFields.forEach((field) => {
        extras[field.key] = form[field.key] || '';
      });
      const response = await api.post('/training/certificates/issue', {
        enrollmentId: enrollment._id,
        templateId: form.templateId || undefined,
        extras,
      });
      const issued = response.data?.data || response.data;
      if (issued?._id) {
        await previewIssuedCertificate(issued._id, issued.certificateNumber);
      }
      onSuccess?.(issued);
    } catch (e) {
      onAlert(e.response?.data?.message || 'Failed to issue certificate', 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BSModal
      show={show}
      onClose={onClose}
      title={`Issue Certificate - ${enrollment.enrollmentNumber || enrollment.batch?.batchNumber || ''}`}
      size="modal-xl"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Issuing...' : 'Issue Certificate'}
          </button>
        </>
      }
    >
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Template</label>
          <select className="form-select" value={form.templateId} onChange={e => setField('templateId', e.target.value)}>
            <option value="">Default Template</option>
            {templates.map(template => (
              <option key={template._id} value={template._id}>
                {template.name}{template.isDefault ? ' (Default)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Student Name</label>
          <input className="form-control" value={form.studentName} onChange={e => setField('studentName', e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Student Email</label>
          <input className="form-control" value={form.studentEmail} onChange={e => setField('studentEmail', e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Course Name</label>
          <input className="form-control" value={form.courseName} onChange={e => setField('courseName', e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Batch ID / Number</label>
          <input className="form-control" value={form.batchNumber} onChange={e => setField('batchNumber', e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Completion Date</label>
          <input className="form-control" type="date" value={form.completionDate} onChange={e => setField('completionDate', e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label">Grade</label>
          <input className="form-control" value={form.grade} onChange={e => setField('grade', e.target.value)} placeholder="Optional" />
        </div>
        <div className="col-md-3">
          <label className="form-label">Issuer Name</label>
          <input className="form-control" value={form.issuerName} onChange={e => setField('issuerName', e.target.value)} placeholder="Optional" />
        </div>
        <div className="col-md-3">
          <label className="form-label">Issuer Title</label>
          <input className="form-control" value={form.issuerTitle} onChange={e => setField('issuerTitle', e.target.value)} placeholder="Optional" />
        </div>
        <div className="col-md-3">
          <label className="form-label">Enrollment No.</label>
          <input className="form-control" value={enrollment.enrollmentNumber || ''} disabled />
        </div>
        <div className="col-md-6">
          <label className="form-label">Certificate Title</label>
          <input className="form-control" value={form.certificateTitle} onChange={e => setField('certificateTitle', e.target.value)} placeholder="Optional" />
        </div>
        <div className="col-md-6">
          <label className="form-label">Subtitle</label>
          <input className="form-control" value={form.subtitle} onChange={e => setField('subtitle', e.target.value)} placeholder="Optional" />
        </div>
      </div>

      <div className="border rounded p-3 mt-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div className="fw-semibold">Custom Fields</div>
            <div className="small text-muted">Template-driven fields can be filled here, and admin can add more if needed.</div>
          </div>
          {hiddenTemplateFields.length > 0 && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setHiddenTemplateFields([])}>
              Restore Template Fields
            </button>
          )}
        </div>

        {visibleCustomFields.length === 0 ? (
          <div className="small text-muted mb-3">No custom fields selected for this certificate.</div>
        ) : (
          <div className="row g-3 mb-3">
            {visibleCustomFields.map((field) => (
              <div className="col-md-6" key={field.key}>
                <label className="form-label d-flex justify-content-between align-items-center">
                  <span>{field.label || field.key}</span>
                  <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeIssueField(field)}>
                    Remove
                  </button>
                </label>
                <input
                  className="form-control"
                  value={form[field.key] || ''}
                  onChange={e => setField(field.key, e.target.value)}
                  placeholder={field.label || field.key}
                />
              </div>
            ))}
          </div>
        )}

        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Field Label</label>
            <input className="form-control" value={newField.label} onChange={e => setNewField(prev => ({ ...prev, label: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Field Key</label>
            <input className="form-control" value={newField.key} onChange={e => setNewField(prev => ({ ...prev, key: e.target.value }))} placeholder="example_field" />
          </div>
          <div className="col-md-2">
            <label className="form-label">Type</label>
            <select className="form-select" value={newField.type} onChange={e => setNewField(prev => ({ ...prev, type: e.target.value }))}>
              <option value="text">Text</option>
              <option value="stars">Stars</option>
            </select>
          </div>
          <div className="col-md-2">
            <button type="button" className="btn btn-outline-primary w-100" onClick={addIssueField}>
              Add Field
            </button>
          </div>
        </div>
      </div>
    </BSModal>
  );
};

export default function Enrollments() {
  const { user } = useAuth();
const location = useLocation();
  const [searchParams] = useSearchParams();

  const role = user?.roleKey || user?.role || '';
  const userRoleKeys = [role, ...(user?.additionalRoleKeys || [])];
  const canWrite = userRoleKeys.some(r => ['admin', 'manager', 'team_lead'].includes(r)) || user?.userType === 'super_admin';
  const canAdmin = userRoleKeys.some(r => ['admin'].includes(r)) || user?.userType === 'super_admin';

  // Data
  const [enrollments,  setEnrollments]  = useState([]);
  const [courses,      setCourses]      = useState([]);
  const [batches,      setBatches]      = useState([]);
  const [students,     setStudents]     = useState([]);
  const [certificateTemplates, setCertificateTemplates] = useState([]);
  const [batchFilter,  setBatchFilter]  = useState('');
  const [stats,        setStats]        = useState(null);
  const [years,        setYears]        = useState([]);
  const [activeYear,   setActiveYear]   = useState('all');

  // UI state
  const [loading,      setLoading]      = useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayStatus, setFilterPayStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [enrollmentCategory, setEnrollmentCategory] = useState(() => searchParams.get('category') === 'college_free' || searchParams.get('source') === 'open_access' ? 'college_free' : 'course');
  const enrollmentRequest = useRef(0);
  const [columnFilters, setColumnFilters] = useState({
    student: '',
    courseBatch: '',
    joinFrom: '',
    joinTo: '',
    fees: '',
    status: '',
    payment: '',
  });
  const [activeHeaderFilter, setActiveHeaderFilter] = useState('');
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalCount,   setTotalCount]   = useState(0);

  // View toggle (table ↔ analytics). Default = table; persisted
  // per-user-per-page in localStorage. Analytics is only
  // available to management roles.
  const [view, setView] = useState(() => readViewPreference('enrollment', 'table'));
  const canSeeAnalytics = canWrite;

  // Alert
  const [alert, setAlert] = useState({ show: false, msg: '', type: 'success' });
  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  // Dialogs
  const [dlgPayHist,   setDlgPayHist]   = useState(null);
  const [dlgInstalment,setDlgInstalment]= useState(null);
  const [dlgReceipt,   setDlgReceipt]   = useState({ enr: null, paymentId: null });
  const [dlgAddPay,    setDlgAddPay]    = useState(null);
  const [dlgCancel,    setDlgCancel]    = useState(null);
  const [dlgStatus,    setDlgStatus]    = useState(null);
  const [dlgFeeInfo,   setDlgFeeInfo]   = useState(null);
  const [dlgEdit,      setDlgEdit]      = useState(null);
  const [dlgIssueCert, setDlgIssueCert] = useState(null);
  const [dlgMembership, setDlgMembership] = useState(null);

  // New enrollment form
  const [enrForm,      setEnrForm]      = useState(blankEnrForm);
  const [formBatches,  setFormBatches]  = useState([]);
  const [saving,       setSaving]       = useState(false);

  // ── Open form from URL params (e.g. from Batches "Enroll Now" or Enquiry "Enroll") ──
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const batchId    = params.get('batchId');
    const courseId   = params.get('courseId');
    const fromEnquiry = params.get('from') === 'enquiry';
    const enquiryId  = params.get('enquiryId');
    const name       = params.get('name') || '';
    const phone      = params.get('phone') || '';
    const email      = params.get('email') || '';
    const viewStudents = params.get('view') === 'students';

    if (viewStudents) {
      setShowForm(false);
      return;
    }

    if ((batchId || fromEnquiry) && canWrite) {
      const [firstName, ...rest] = name.split(' ');
      setEnrForm(f => ({
        ...f,
        batchId:   batchId || '',
        courseId:  courseId || '',
        isNewStudent: fromEnquiry ? true : f.isNewStudent,
        firstName: firstName || f.firstName,
        lastName:  rest.join(' ') || f.lastName,
        phone:     phone || f.phone,
        email:     email || f.email,
        notes:     fromEnquiry && enquiryId ? `Converted from enquiry ${enquiryId}` : f.notes,
      }));
      setShowForm(true);
    }
  }, [location.search, canWrite]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch reference data ──────────────────────────────────────────────────

  useEffect(() => {
    const urlBatchId = searchParams.get('batchId');
    const urlSource = searchParams.get('source');
    if (urlBatchId) setBatchFilter(urlBatchId);
    if (urlSource) setFilterSource(urlSource);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/training/courses', { params: { limit: 200 } })
      .then(r => setCourses(r.data?.data || r.data?.records || []))
      .catch(() => {});
    api.get('/training/batches', { params: { limit: 500 } })
      .then(r => setBatches(r.data?.data || r.data?.records || []))
      .catch(() => {});
    api.get('/training/enrollments/students')
      .then(r => setStudents(r.data?.records || r.data?.data || []))
      .catch(() => {});
    api.get('/training/certificates/templates')
      .then(r => setCertificateTemplates(r.data?.data || r.data?.records || []))
      .catch(() => {});
    // Stats are loaded inside fetchEnrollments so they stay in sync with the list
    // Build year list
    const yr = new Date().getFullYear();
    setYears([yr + 1, yr, yr - 1, yr - 2]);
  }, [canAdmin]);

  // ── Fetch enrollments ─────────────────────────────────────────────────────

  const fetchEnrollments = useCallback(async () => {
    const requestId = ++enrollmentRequest.current;
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        search: search || undefined,
        status: filterStatus || undefined,
        paymentStatus: filterPayStatus || undefined,
        source: filterSource || undefined,
        category: enrollmentCategory,
      };
      if (activeYear !== 'all') params.year = activeYear;
      if (batchFilter) params.batch = batchFilter;
      const fetches = [api.get('/training/enrollments', { params })];
      if (canAdmin) fetches.push(api.get('/training/enrollments/stats/overview').catch(() => null));
      const [r, s] = await Promise.all(fetches);
      if (requestId !== enrollmentRequest.current) return;
      if (r.data?.enrollmentCategory !== enrollmentCategory || r.data?.enrollmentCategoryVersion !== 2) {
        setEnrollments([]);
        setTotalPages(1);
        setTotalCount(0);
        setAlert({ show: true, type: 'danger', msg: 'The running backend has not confirmed the enrollment category. Deploy the latest enrollment route and restart the process serving port 5030.' });
        setLoading(false);
        return;
      }
      setEnrollments(r.data?.data || r.data?.records || []);
      setTotalPages(r.data?.pagination?.pages || r.data?.pagination?.totalPages || 1);
      setTotalCount(r.data?.pagination?.total || r.data?.pagination?.count || 0);
      if (s) setStats(s.data?.data || s.data || null);
    } catch (_) {}
    if (requestId === enrollmentRequest.current) setLoading(false);
  }, [page, search, filterStatus, filterPayStatus, filterSource, activeYear, batchFilter, canAdmin, enrollmentCategory]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);
  const enrollmentColumnOptions = {
    student: [...new Set(enrollments.map(enr => enr.student ? `${enr.student.firstName || ''} ${enr.student.lastName || ''}`.trim() : '—').filter(Boolean))].map(v => ({ value: v, label: v })),
    courseBatch: buildCountOptions(enrollments.flatMap((enr) => [
      enr.course?.courseName || enr.course?.name || '',
      enr.course?.courseCode || '',
      enr.batch?.batchNumber || '',
    ])),
    fees: buildCountOptions(enrollments.map((enr) => {
      const paid = enr.fees?.paid ?? enr.fees?.paidAmount ?? 0;
      const total = enr.fees?.netPayable ?? enr.fees?.total ?? enr.fees?.totalFees ?? 0;
      return `${fmt(paid)} / ${fmt(total)}`;
    })),
    student: buildCountOptions(enrollments.flatMap((enr) => {
      const fullName = enr.student ? `${enr.student.firstName || ''} ${enr.student.lastName || ''}`.trim() : '';
      return [fullName, enr.student?.email || ''];
    })),
    status: ENROLLMENT_STATUSES.map(s => ({ value: s.value, label: s.label })),
    payment: [
      { value: 'pending', label: 'Pending' },
      { value: 'partial', label: 'Partial' },
      { value: 'completed', label: 'Completed' },
      { value: 'overdue', label: 'Overdue' },
    ],
  };
  const filteredEnrollments = enrollments.filter((enr) => {
    const displayStudentName = enr.student ? `${enr.student.firstName || ''} ${enr.student.lastName || ''}`.trim() : '—';
    const studentText = `${displayStudentName} ${enr.student?.email || ''}`.toLowerCase();
    const courseBatchText = `${enr.course?.courseName || enr.course?.name || ''} ${enr.batch?.batchNumber || ''}`.toLowerCase();
    const paid = enr.fees?.paid ?? enr.fees?.paidAmount ?? 0;
    const total = enr.fees?.netPayable ?? enr.fees?.total ?? enr.fees?.totalFees ?? 0;
    const feesText = `${fmt(paid)} ${fmt(total)}`.toLowerCase();
    const joiningDate = enr.joiningDate ? new Date(enr.joiningDate).toISOString().split('T')[0] : '';
    if (columnFilters.student && !studentText.includes(columnFilters.student.toLowerCase())) return false;
    if (columnFilters.courseBatch && !courseBatchText.includes(columnFilters.courseBatch.toLowerCase())) return false;
    if (columnFilters.joinFrom && (!joiningDate || joiningDate < columnFilters.joinFrom)) return false;
    if (columnFilters.joinTo && (!joiningDate || joiningDate > columnFilters.joinTo)) return false;
    if (columnFilters.fees && !feesText.includes(columnFilters.fees.toLowerCase())) return false;
    if (columnFilters.status && String(enr.status || '').toLowerCase() !== columnFilters.status.toLowerCase()) return false;
    if (columnFilters.payment && String(enr.paymentStatus || '').toLowerCase() !== columnFilters.payment.toLowerCase()) return false;
    return true;
  });
  const setColumnFilter = (key, value) => setColumnFilters(prev => ({ ...prev, [key]: value }));
  const clearColumnFilters = () => {
    setColumnFilters({ student: '', courseBatch: '', joinFrom: '', joinTo: '', fees: '', status: '', payment: '' });
    setActiveHeaderFilter('');
  };
  const PAGE_LIMIT = 20;
  const pageStart = totalCount === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
  const pageEnd = Math.min((page - 1) * PAGE_LIMIT + filteredEnrollments.length, totalCount);
  const renderHeaderFilter = (key, label, type = 'text', options = []) => {
    const isActive = activeHeaderFilter === key;
    const hasValue = key === 'joining' ? !!(columnFilters.joinFrom || columnFilters.joinTo) : !!columnFilters[key];
    const normalizedValue = (columnFilters[key] || '').toLowerCase();
    const filteredOptions = options.filter(option => !normalizedValue || String(option.label || option.value || '').toLowerCase().includes(normalizedValue));
    return (
      <div className="d-inline-flex align-items-center gap-1 position-relative">
        <span>{label}</span>
        <button type="button" className={`btn btn-sm p-0 border-0 bg-transparent ${hasValue ? 'text-primary' : 'text-muted'}`} style={{ width: 18, height: 18, lineHeight: 1 }} onClick={() => setActiveHeaderFilter(cur => cur === key ? '' : key)} title={`Filter ${label}`}>
          <i className={`bi ${hasValue ? 'bi-funnel-fill' : 'bi-funnel'}`} />
        </button>
        {isActive && (
          <div className="position-absolute bg-white border rounded-3 shadow-sm p-2" style={{ top: 'calc(100% + 6px)', left: 0, minWidth: 220, zIndex: 20 }}>
            {type === 'select' ? (
              <select className="form-select form-select-sm" value={columnFilters[key]} onChange={e => setColumnFilter(key, e.target.value)}>
                <option value="">All</option>
                {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            ) : type === 'dateRange' ? (
              <div className="d-flex flex-column gap-2">
                <div><label className="form-label small text-muted mb-1">From</label><input type="date" className="form-control form-control-sm" value={columnFilters.joinFrom} onChange={e => setColumnFilters(f => ({ ...f, joinFrom: e.target.value }))} /></div>
                <div><label className="form-label small text-muted mb-1">To</label><input type="date" className="form-control form-control-sm" value={columnFilters.joinTo} onChange={e => setColumnFilters(f => ({ ...f, joinTo: e.target.value }))} /></div>
              </div>
            ) : type === 'datalist' ? (
              <>
                <input className="form-control form-control-sm" placeholder={`Filter ${label.toLowerCase()}`} value={columnFilters[key]} onChange={e => setColumnFilter(key, e.target.value)} />
                <div className="list-group list-group-flush border rounded-2 mt-2 overflow-auto" style={{ maxHeight: 180 }}>
                  {filteredOptions.length ? filteredOptions.map(option => (
                    <button key={option.value} type="button" className="list-group-item list-group-item-action text-start py-2 px-2" onClick={() => { setColumnFilter(key, option.value); setActiveHeaderFilter(''); }}>{option.label}</button>
                  )) : <div className="small text-muted px-2 py-2">No suggestions</div>}
                </div>
              </>
            ) : (
              <input className="form-control form-control-sm" placeholder={`Filter ${label.toLowerCase()}`} value={columnFilters[key]} onChange={e => setColumnFilter(key, e.target.value)} />
            )}
            <div className="d-flex justify-content-between mt-2">
              <button type="button" className="btn btn-link btn-sm text-decoration-none p-0" onClick={() => { if (key === 'joining') setColumnFilters(f => ({ ...f, joinFrom: '', joinTo: '' })); else setColumnFilter(key, ''); }}>Clear</button>
              <button type="button" className="btn btn-link btn-sm text-decoration-none p-0" onClick={() => setActiveHeaderFilter('')}>Close</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Form batch filter ─────────────────────────────────────────────────────

  useEffect(() => {
    if (enrForm.courseId) {
      setFormBatches(batches.filter(b => b.courseId === enrForm.courseId || b.course?._id === enrForm.courseId));
    } else {
      setFormBatches([]);
    }
  }, [enrForm.courseId, batches]);

  const setEnrField = (k, v) => setEnrForm(p => ({ ...p, [k]: v }));

  // ── Create enrollment ─────────────────────────────────────────────────────

  const handleCreateEnrollment = async () => {
    if (!enrForm.courseId)  { showAlert('Please select a course.',  'danger'); return; }
    if (!enrForm.batchId)   { showAlert('Please select a batch.',   'danger'); return; }
    if (!enrForm.totalFees) { showAlert('Please enter total fees.', 'danger'); return; }
    if (enrForm.isNewStudent && (!enrForm.firstName || !enrForm.email)) {
      showAlert('First name and email are required for new students.', 'danger'); return;
    }
    if (!enrForm.isNewStudent && !enrForm.studentId) {
      showAlert('Please select an existing student.', 'danger'); return;
    }
    setSaving(true);
    try {
      // Backend shape: { student, newStudent, course, batch, fees:{total,discount},
      //                  paymentType:'one_time'|'emi', paymentMethod, emiDetails }
      const total = Number(enrForm.totalFees) || 0;
      const discount = Number(enrForm.discountAmount) || 0;
      const payload = {
        course:        enrForm.courseId,
        batch:         enrForm.batchId,
        joiningDate:   enrForm.joiningDate || undefined,
        notes:         enrForm.notes || undefined,
        paymentType:   enrForm.paymentMode === 'emi' ? 'emi' : 'one_time',
        paymentMethod: enrForm.paymentMethod || 'cash',
        fees: { total, discount },
      };
      if (enrForm.paymentMode === 'emi' && enrForm.emiMonths) {
        payload.emiDetails = {
          numberOfInstallments: Number(enrForm.emiMonths),
          startDate: enrForm.joiningDate || undefined,
        };
      }
      if (enrForm.isNewStudent) {
        payload.newStudent = {
          firstName: enrForm.firstName,
          lastName:  enrForm.lastName,
          email:     enrForm.email,
          phone:     enrForm.phone,
        };
      } else {
        payload.student = enrForm.studentId;
      }
      const r = await api.post('/training/enrollments', payload);
      if (r.data?.success !== false) {
        showAlert('Enrollment created successfully.', 'success');
        setShowForm(false);
        setEnrForm(blankEnrForm);
        fetchEnrollments();
      } else {
        showAlert(r.data?.message || 'Failed to create enrollment.', 'danger');
      }
    } catch (e) { showAlert(e.response?.data?.message || 'Failed to create enrollment.', 'danger'); }
    setSaving(false);
  };

  // ── Handle update from dialogs ────────────────────────────────────────────

  const handleUpdated = () => { fetchEnrollments(); };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (enr) => {
    if (!window.confirm(`Delete enrollment ${enr.enrollmentNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/training/enrollments/${enr._id}`);
      showAlert('Enrollment deleted.', 'success');
      fetchEnrollments();
    } catch (e) { showAlert(e.response?.data?.message || 'Delete failed.', 'danger'); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const pct = (paid, total) => total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  const studentName = (enr) => enr.student ? `${enr.student.firstName || ''} ${enr.student.lastName || ''}`.trim() : '—';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show: false }))} />

      <PageHeader
        title="Enrollments"
        icon="bi-person-check"
        subtitle={batchFilter ? `Batch: ${batches.find(b => b._id === batchFilter)?.batchNumber || batchFilter}` : "Manage student enrollments, fees & payments"}
        actions={
          canWrite && (
            <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(p => !p); setEnrForm(blankEnrForm); }}>
              <i className="bi bi-plus-lg me-1" />
              {showForm ? 'Cancel' : 'New Enrollment'}
            </button>
          )
        }
      />

      {/* View toggle (Table ↔ Analytics) — analytics is management-only */}
      {canSeeAnalytics && (
        <div className="d-flex justify-content-end mb-3">
          <ViewToggle
            storageKey="enrollment"
            value={view}
            onChange={setView}
            options={[
              { value: 'table', label: 'Table View', icon: 'bi-table' },
              { value: 'analytics', label: 'Analytics', icon: 'bi-bar-chart-line-fill' },
              { value: 'collections', label: 'Collections', icon: 'bi-cash-stack' },
            ]}
          />
        </div>
      )}

      {/* Analytics view (renders ABOVE the table) */}
      {canSeeAnalytics && view === 'analytics' && (
        <div className="mb-4">
          <EnrollmentAnalytics />
        </div>
      )}

      {canSeeAnalytics && view === 'collections' && (
        <div className="mb-4">
          <EnrollmentCollectionsAnalytics />
        </div>
      )}

      {/* New Enrollment Form */}
      {showForm && (
        <div className="card border-primary mb-3">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <span><i className="bi bi-person-plus me-2" />New Enrollment</span>
            <button className="btn-close btn-close-white btn-sm" onClick={() => setShowForm(false)} />
          </div>
          <div className="card-body">
            {/* Student source — two clear tabs: existing vs manual entry */}
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted">Student source</label>
              <ul className="nav nav-pills">
                <li className="nav-item me-2">
                  <button
                    type="button"
                    className={`nav-link ${!enrForm.isNewStudent ? 'active' : ''}`}
                    onClick={() => { setEnrField('isNewStudent', false); setEnrField('firstName',''); setEnrField('lastName',''); setEnrField('email',''); setEnrField('phone',''); }}
                  >
                    <i className="bi bi-person-check me-1" />Existing Student
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${enrForm.isNewStudent ? 'active' : ''}`}
                    onClick={() => { setEnrField('isNewStudent', true); setEnrField('studentId', ''); }}
                  >
                    <i className="bi bi-person-plus me-1" />Manual Entry (new student)
                  </button>
                </li>
              </ul>
            </div>

            {enrForm.isNewStudent ? (() => {
              // Duplicate check — flag if someone with this email or phone already exists
              const emailClash = enrForm.email && students.find(s => (s.email || '').toLowerCase() === enrForm.email.toLowerCase().trim());
              const phoneClash = enrForm.phone && students.find(s => (s.phone || '').replace(/\D/g,'') === enrForm.phone.replace(/\D/g,'') && enrForm.phone.replace(/\D/g,'').length >= 8);
              const clash = emailClash || phoneClash;
              return (
              <>
                {clash && (
                  <div className="alert alert-warning py-2 small mb-2 d-flex justify-content-between align-items-center">
                    <div>
                      <i className="bi bi-exclamation-triangle me-1" />
                      <strong>{clash.firstName} {clash.lastName}</strong> is already registered
                      {emailClash && <> with this email</>}
                      {!emailClash && phoneClash && <> with this phone</>}
                      {' '}({clash.email}). You may be creating a duplicate account.
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEnrField('isNewStudent', false);
                        setEnrField('studentId', clash._id);
                        setEnrField('firstName',''); setEnrField('lastName','');
                        setEnrField('email',''); setEnrField('phone','');
                      }}
                    >
                      Use existing
                    </button>
                  </div>
                )}
                <div className="row g-3 mb-3 p-2 border rounded bg-light">
                  <div className="col-md-3 col-6">
                    <label className="form-label">First Name <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={enrForm.firstName} onChange={e => setEnrField('firstName', e.target.value)} />
                  </div>
                  <div className="col-md-3 col-6">
                    <label className="form-label">Last Name</label>
                    <input className="form-control form-control-sm" value={enrForm.lastName} onChange={e => setEnrField('lastName', e.target.value)} />
                  </div>
                  <div className="col-md-3 col-6">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input className={`form-control form-control-sm ${emailClash ? 'is-invalid' : ''}`}
                           type="email" value={enrForm.email} onChange={e => setEnrField('email', e.target.value)} />
                  </div>
                  <div className="col-md-3 col-6">
                    <label className="form-label">Phone</label>
                    <input className={`form-control form-control-sm ${phoneClash && !emailClash ? 'is-invalid' : ''}`}
                           value={enrForm.phone} onChange={e => setEnrField('phone', e.target.value)} />
                  </div>
                </div>
              </>);
            })() : (
              <div className="mb-3">
                <label className="form-label">Select Student <span className="text-danger">*</span></label>
                {students.length === 0 ? (
                  <div className="alert alert-warning py-2 small mb-0">No students found in the system.</div>
                ) : (
                  <select
                    className="form-select form-select-sm"
                    value={enrForm.studentId}
                    onChange={e => setEnrField('studentId', e.target.value)}
                  >
                    <option value="">— Select a student —</option>
                    {students.map(s => (
                      <option key={s._id} value={s._id}>
                        {s.firstName} {s.lastName}{s.email ? ` (${s.email})` : ''}{s.roleKey ? ` — ${s.roleKey}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Course + Batch + Date */}
            <div className="row g-3 mb-3">
              <div className="col-md-4 col-6">
                <label className="form-label">Course <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" value={enrForm.courseId}
                        onChange={e => {
                          const cid = e.target.value;
                          setEnrField('courseId', cid);
                          setEnrField('batchId', '');
                          // Auto-fill total fees from the chosen course (read-only downstream).
                          // Reset discount when course changes so it doesn't silently apply
                          // to a different course's fees.
                          const c = courses.find(x => x._id === cid);
                          setEnrField('totalFees', c?.totalFees ? String(c.totalFees) : '');
                          setEnrField('discountAmount', '');
                        }}>
                  <option value="">Select Course</option>
                  {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name} {c.courseCode ? `(${c.courseCode})` : ''}</option>)}
                </select>
              </div>
              <div className="col-md-4 col-6">
                <label className="form-label">Batch <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" value={enrForm.batchId} onChange={e => setEnrField('batchId', e.target.value)} disabled={!enrForm.courseId}>
                  <option value="">{enrForm.courseId ? 'Select Batch' : '— select course first —'}</option>
                  {formBatches.map(b => <option key={b._id} value={b._id}>{b.batchNumber}</option>)}
                </select>
              </div>
              <div className="col-md-4 col-6">
                <label className="form-label">Joining Date</label>
                <input className="form-control form-control-sm" type="date" value={enrForm.joiningDate} onChange={e => setEnrField('joiningDate', e.target.value)} />
              </div>
            </div>

            {/* Fees */}
            <div className="row g-3 mb-3">
              <div className="col-md-3 col-6">
                <label className="form-label">
                  Total Fees (₹) <span className="text-danger">*</span>
                  <i className="bi bi-lock-fill text-muted ms-1" title="Auto-filled from the selected course" style={{ fontSize: 11 }}></i>
                </label>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  value={enrForm.totalFees}
                  readOnly
                  tabIndex={-1}
                  placeholder="Select a course first"
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  title="Set on the course itself. Use the Discount field below to adjust what the student pays."
                />
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label">Discount (₹)</label>
                <input className="form-control form-control-sm" type="number" value={enrForm.discountAmount} onChange={e => setEnrField('discountAmount', e.target.value)} placeholder="0" />
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label">Payment Mode</label>
                <select className="form-select form-select-sm" value={enrForm.paymentMode} onChange={e => setEnrField('paymentMode', e.target.value)}>
                  <option value="full">Full Payment</option>
                  <option value="partial">Partial</option>
                  <option value="emi">EMI</option>
                </select>
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label">Payment Method <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" value={enrForm.paymentMethod}
                        onChange={e => setEnrField('paymentMethod', e.target.value)}>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="net_banking">Net Banking</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="dd">Demand Draft</option>
                  <option value="online">Online</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {enrForm.paymentMode === 'emi' && (
                <div className="col-md-3 col-6">
                  <label className="form-label">EMI Months</label>
                  <input className="form-control form-control-sm" type="number" min="1" value={enrForm.emiMonths} onChange={e => setEnrField('emiMonths', e.target.value)} />
                </div>
              )}
            </div>

            {enrForm.totalFees && (
              <div className="alert alert-light py-2 small mb-3">
                Payable: <strong>{fmt((Number(enrForm.totalFees) || 0) - (Number(enrForm.discountAmount) || 0))}</strong>
                {enrForm.paymentMode === 'emi' && enrForm.emiMonths && (
                  <span className="ms-3">≈ {fmt(Math.ceil(((Number(enrForm.totalFees) || 0) - (Number(enrForm.discountAmount) || 0)) / Number(enrForm.emiMonths)))}/month</span>
                )}
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">Notes</label>
              <textarea className="form-control form-control-sm" rows={2} value={enrForm.notes} onChange={e => setEnrField('notes', e.target.value)} />
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleCreateEnrollment} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-1" />Creating…</> : <><i className="bi bi-check-lg me-1" />Create Enrollment</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table View — filters + stats + table + pagination are
          all wrapped in `view === 'table'` so the user can flip
          to the analytics dashboard without losing any of the
          existing controls. The `canSeeAnalytics` flag above
          decides whether the toggle is even rendered. */}
      {(!canSeeAnalytics || view === 'table') && (
      <>
      {/* Stats */}
      {canAdmin && stats && (
        <div className="row g-3 mb-3">
          <div className="col-6 col-md-2">
            <StatCard title="Total Enrollments" value={stats.enrollments?.total ?? 0} icon="bi-person-check" color="primary" />
          </div>
          <div className="col-6 col-md-2">
            <StatCard title="Active" value={stats.enrollments?.active ?? 0} icon="bi-person-check-fill" color="success" />
          </div>
          <div className="col-6 col-md-2">
            <StatCard title="LMS / Package" value={stats.enrollments?.lms ?? 0} icon="bi-collection-play" color="info" />
          </div>
          <div className="col-6 col-md-2">
            <StatCard title="Free Course Students" value={stats.enrollments?.freeCourse ?? 0} icon="bi-play-circle-fill" color="primary" />
          </div>
          <div className="col-6 col-md-2">
            <StatCard title="Monthly LMS Intake" value={stats.enrollments?.monthlyLmsIntake ?? 0} icon="bi-graph-up-arrow" color="secondary" />
          </div>
          <div className="col-6 col-md-2">
            <StatCard title="Revenue Collected" value={fmt(stats.revenue?.totalPaid)} icon="bi-currency-rupee" color="info" />
          </div>
          <div className="col-6 col-md-2">
            <StatCard title="Pending Fees" value={fmt(stats.revenue?.totalBalance)} icon="bi-hourglass-split" color="warning" />
          </div>
        </div>
      )}

      {/* Enrollment type tabs */}
      <ul className="nav nav-pills gap-2 mb-3">
        <li className="nav-item">
          <button className={`nav-link ${enrollmentCategory === 'course' ? 'active' : ''}`} onClick={() => { setEnrollmentCategory('course'); setFilterSource(''); setFilterPayStatus(''); setPage(1); }}>Course Enrollments</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${enrollmentCategory === 'college_free' ? 'active' : ''}`} onClick={() => { setEnrollmentCategory('college_free'); setFilterSource(''); setFilterPayStatus(''); setPage(1); }}>Free Course Enrollment</button>
        </li>
      </ul>

      {/* Year Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${activeYear === 'all' ? 'active' : ''}`} onClick={() => { setActiveYear('all'); setPage(1); }}>All</button>
        </li>
        {years.map(y => (
          <li className="nav-item" key={y}>
            <button className={`nav-link ${activeYear === String(y) ? 'active' : ''}`} onClick={() => { setActiveYear(String(y)); setPage(1); }}>{y}</button>
          </li>
        ))}
      </ul>

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <div className="input-group input-group-sm">
            <span className="input-group-text"><i className="bi bi-search" /></span>
            <input
              className="form-control"
              placeholder="Search by name, enrollment no., email…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
            {search && (
              <button className="btn btn-outline-secondary" onClick={() => { setSearch(''); setPage(1); }}>
                <i className="bi bi-x" />
              </button>
            )}
          </div>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={batchFilter} onChange={e => { setBatchFilter(e.target.value); setPage(1); }}>
            <option value="">All Batches</option>
            {batches.map(b => <option key={b._id} value={b._id}>{b.batchNumber} ({b.course?.courseName || b.courseName || 'Unknown Course'})</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {ENROLLMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filterPayStatus} onChange={e => { setFilterPayStatus(e.target.value); setPage(1); }}>
            <option value="">All Payment Statuses</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }}>
            <option value="">All Sources</option>
            <option value="lms">LMS / Package</option>
            <option value="membership">Membership</option>
            <option value="promotion">Promotion</option>
            <option value="open_access">Free Course</option>
            <option value="manual">Manual</option>
            <option value="enquiry">Enquiry</option>
            <option value="admin">Admin</option>
            <option value="import">Import</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : filteredEnrollments.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2" />
              No enrollments found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>{renderHeaderFilter('student', 'Student', 'datalist', enrollmentColumnOptions.student)}</th>
                    <th>{renderHeaderFilter('courseBatch', 'Course / Batch', 'datalist', enrollmentColumnOptions.courseBatch)}</th>
                    <th>{renderHeaderFilter('joining', 'Joining', 'dateRange')}</th>
                    <th>{renderHeaderFilter('fees', 'Fees Progress', 'datalist', enrollmentColumnOptions.fees)}</th>
                    <th>{renderHeaderFilter('status', 'Status', 'select', enrollmentColumnOptions.status)}</th>
                    <th>{renderHeaderFilter('payment', 'Payment', 'select', enrollmentColumnOptions.payment)}</th>
                    <th className="text-end">
                      <button className="btn btn-outline-secondary btn-sm" type="button" onClick={clearColumnFilters}>Clear</button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnrollments.map((enr, idx) => {
                    const paid  = enr.fees?.paid  ?? enr.fees?.paidAmount ?? 0;
                    // Use netPayable (after discount) as the 100% mark so a
                    // fully-paid discounted enrollment shows a full green bar.
                    const total = enr.fees?.netPayable ?? enr.fees?.total ?? enr.fees?.totalFees ?? 0;
                    const p = pct(paid, total);
                    return (
                      <tr key={enr._id}>
                        <td>
                          <small className="text-muted">{enr.enrollmentNumber || `#${idx + 1}`}</small>
                        </td>
                        <td>
                          <div className="fw-semibold">{studentName(enr)}</div>
                          <small className="text-muted">{enr.student?.email || ''}</small>
                        </td>
                        <td>
                          <CourseNameBadge courseName={enr.course?.courseName || enr.course?.name || '—'} courseCode={enr.course?.courseCode || ''} courseColor={enr.course?.courseColor} compact />
                          <div className="small text-muted">{enr.batch?.batchNumber || '—'}</div>
                          {['membership', 'promotion'].includes(String(enr.source || '')) && (
                            <button
                              type="button"
                              className={`badge border-0 ${enr.source === 'promotion' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setDlgMembership(enr)}
                            >
                              {enr.source === 'promotion'
                                ? 'LMS Promotion'
                                : (enr.sourceMeta?.membershipPlanName || enr.sourceMeta?.membershipPlanCode || 'LMS Membership')}
                            </button>
                          )}
                          {String(enr.source || '') === 'open_access' && (
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">Free Course</span>
                          )}
                          {enrollmentCategory === 'college_free' && enr.student?.collegeAccess?.partnerCollegeName && (
                            <div className="small text-muted">{enr.student.collegeAccess.partnerCollegeName}</div>
                          )}
                        </td>
                        <td><small>{fmtDate(enr.joiningDate)}</small></td>
                        <td style={{ minWidth: 140 }}>
                          {String(enr.source || '') === 'open_access' ? (
                            <div className="small fw-semibold text-primary">Free access</div>
                          ) : <><div className="d-flex justify-content-between small mb-1">
                            <span>{fmt(paid)}</span>
                            <span
                              className="text-muted"
                              style={{ cursor: canAdmin ? 'pointer' : 'default' }}
                              onClick={() => canAdmin ? setDlgFeeInfo(enr) : undefined}
                            >
                              {fmt(total)}
                              {canAdmin && <i className="bi bi-info-circle ms-1" />}
                            </span>
                          </div>
                          <div className="progress" style={{ height: 6 }}>
                            <div
                              className={`progress-bar bg-${p >= 100 ? 'success' : p > 50 ? 'info' : p > 0 ? 'warning' : 'danger'}`}
                              style={{ width: `${p}%` }}
                            />
                          </div>
                          </>}
                        </td>
                        <td>
                          <span className={`badge bg-${ENROLLMENT_STATUS_BS[enr.status] || 'secondary'}`}>
                            {enr.status || '—'}
                          </span>
                        </td>
                        <td>
                          {String(enr.source || '') === 'open_access' ? (
                            <span className="badge bg-primary-subtle text-primary">Not applicable</span>
                          ) : (
                          <span className={`badge bg-${PAYMENT_STATUS_BS[enr.paymentStatus] || 'secondary'}`}>
                            {enr.paymentStatus || '—'}
                          </span>
                          )}
                        </td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                          {/* Payment history */}
                          {String(enr.source || '') !== 'open_access' && (
                            <button className="btn btn-outline-secondary btn-sm me-1" title="Payment History" onClick={() => setDlgPayHist(enr)}>
                              <i className="bi bi-clock-history" />
                            </button>
                          )}
                          {/* Instalment schedule */}
                          {enr.fees?.paymentMode === 'emi' && (
                            <button className="btn btn-outline-info btn-sm me-1" title="EMI Schedule" onClick={() => setDlgInstalment(enr)}>
                              <i className="bi bi-calendar3" />
                            </button>
                          )}
                          {/* Add payment */}
                          {canWrite && String(enr.source || '') !== 'open_access' && enr.paymentStatus !== 'completed' && enr.status !== 'cancelled' && (
                            <button className="btn btn-outline-success btn-sm me-1" title="Add Payment" onClick={() => setDlgAddPay(enr)}>
                              <i className="bi bi-cash-coin" />
                            </button>
                          )}
                          {/* Edit */}
                          {canWrite && (
                            <button className="btn btn-outline-primary btn-sm me-1" title="Edit" onClick={() => setDlgEdit(enr)}>
                              <i className="bi bi-pencil" />
                            </button>
                          )}
                          {/* Change status */}
                          {canWrite && (
                            <button className="btn btn-outline-warning btn-sm me-1" title="Change Status" onClick={() => setDlgStatus(enr)}>
                              <i className="bi bi-arrow-repeat" />
                            </button>
                          )}
                          {canAdmin && enr.status === 'completed' && (
                            <button className="btn btn-outline-dark btn-sm me-1" title="Issue Certificate" onClick={() => setDlgIssueCert(enr)}>
                              <i className="bi bi-award" />
                            </button>
                          )}
                          {/* Cancel */}
                          {canWrite && enr.status !== 'cancelled' && (
                            <button className="btn btn-outline-danger btn-sm me-1" title="Cancel Enrollment" onClick={() => setDlgCancel(enr)}>
                              <i className="bi bi-x-circle" />
                            </button>
                          )}
                          {/* Delete (admin only) */}
                          {canAdmin && (
                            <button className="btn btn-outline-danger btn-sm" title="Delete" onClick={() => handleDelete(enr)}>
                              <i className="bi bi-trash" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer d-flex justify-content-between align-items-center py-2">
          <small className="text-muted">
            Showing {filteredEnrollments.length ? `${pageStart}-${pageEnd}` : '0'} of {totalCount} total
            {' '}· Page {page} of {totalPages}
            {' '}· {filteredEnrollments.length} row{filteredEnrollments.length === 1 ? '' : 's'} on this page
          </small>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-3 d-flex justify-content-center">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPage(p)}>{p}</button>
              </li>
            ))}
            <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
            </li>
          </ul>
        </nav>
      )}
      </>
      )}

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}

      <PaymentHistoryDialog
        show={!!dlgPayHist}
        enrollment={dlgPayHist}
        onClose={() => setDlgPayHist(null)}
        onViewReceipt={(enr, paymentId) => {
          setDlgPayHist(null);
          setDlgReceipt({ enr, paymentId });
        }}
        onAlert={showAlert}
      />

      <InstalmentScheduleDialog
        show={!!dlgInstalment}
        enrollment={dlgInstalment}
        onClose={() => setDlgInstalment(null)}
      />

      <ReceiptDialog
        show={!!dlgReceipt.enr}
        enrollment={dlgReceipt.enr}
        paymentId={dlgReceipt.paymentId}
        onClose={() => setDlgReceipt({ enr: null, paymentId: null })}
      />

      <AddPaymentDialog
        show={!!dlgAddPay}
        enrollment={dlgAddPay}
        onClose={() => setDlgAddPay(null)}
        onSuccess={(updated, receipt) => {
          handleUpdated(updated);
          if (receipt?.emailDelivery?.sent) {
            showAlert('Payment recorded and receipt emailed to the student.', 'success');
          } else {
            showAlert('Payment recorded, but the receipt email could not be sent. Use Resend Receipt after checking the student email.', 'warning');
          }
        }}
        onAlert={showAlert}
      />

      <CancelDialog
        show={!!dlgCancel}
        enrollment={dlgCancel}
        onClose={() => setDlgCancel(null)}
        onSuccess={(updated) => { handleUpdated(updated); showAlert('Enrollment cancelled.', 'warning'); }}
        onAlert={showAlert}
      />

      <StatusChangeDialog
        show={!!dlgStatus}
        enrollment={dlgStatus}
        onClose={() => setDlgStatus(null)}
        onSuccess={(updated) => { handleUpdated(updated); showAlert('Status updated.', 'success'); }}
        onAlert={showAlert}
        canAdmin={canAdmin}
      />

      <FeeInfoDialog
        show={!!dlgFeeInfo}
        enrollment={dlgFeeInfo}
        onClose={() => setDlgFeeInfo(null)}
      />

      <MembershipSubscriptionDialog
        show={!!dlgMembership}
        enrollment={dlgMembership}
        onClose={() => setDlgMembership(null)}
      />

      <EditEnrollmentDialog
        show={!!dlgEdit}
        enrollment={dlgEdit}
        courses={courses}
        batches={batches}
        onClose={() => setDlgEdit(null)}
        onSuccess={(updated) => { handleUpdated(updated); showAlert('Enrollment updated.', 'success'); }}
        onAlert={showAlert}
        canAdmin={canAdmin}
      />

      <IssueCertificateDialog
        show={!!dlgIssueCert}
        enrollment={dlgIssueCert}
        templates={certificateTemplates}
        onClose={() => setDlgIssueCert(null)}
        onSuccess={() => {
          setDlgIssueCert(null);
          showAlert('Certificate issued successfully.', 'success');
        }}
        onAlert={showAlert}
      />
    </div>
  );
}
